import React from "react";
import { motion } from "framer-motion";
import {
  Store,
  MousePointer2,
  MonitorSmartphone,
  ShoppingBag,
  TrendingUp,
  Bot,
  Bell,
  IndianRupee,
  Sparkles,
  Rocket,
  ArrowRight,
  Truck,
  Wallet,
  Smile,
} from "lucide-react";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

/* ---------- Robotic mascot ---------- */
function RobotMascot({ tone = "violet", state = "idle" }) {
  const eye = state === "happy" ? "•" : "◉";
  return (
    <div className="relative">
      <motion.div
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        className="relative w-12 h-12 rounded-2xl btn-primary-grad border border-white/20 flex items-center justify-center shadow-xl shadow-violet-500/40"
      >
        {/* antenna */}
        <span className="absolute -top-2 left-1/2 -translate-x-1/2 w-[2px] h-2 bg-white/70" />
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-pink-300 animate-pulse" />
        {/* face */}
        <div className="flex items-center gap-1.5 text-white text-[11px] font-bold">
          <span>{eye}</span>
          <span>{eye}</span>
        </div>
        {/* mouth */}
        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 w-3 h-[2px] rounded bg-white/80" />
      </motion.div>
      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-violet-500/40 blur-[2px]" />
    </div>
  );
}

/* ---------- Per-step animated scenes ---------- */

function SceneOffline() {
  return (
    <div className="relative h-52 rounded-2xl overflow-hidden border border-white/5 bg-gradient-to-br from-[#12081a] via-[#0b0820] to-[#0a0a1a]">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute left-6 bottom-5">
        <RobotMascot />
      </div>
      <div className="absolute inset-x-0 bottom-5 flex items-end justify-center">
        <motion.div
          initial={{ y: 10, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="w-28 h-7 rounded-t-lg bg-gradient-to-b from-rose-700/40 to-rose-900/40 border-x border-t border-white/10" />
          <div className="w-28 h-16 bg-[#1b1028] border-x border-b border-white/10 flex items-center justify-center">
            <Store className="w-7 h-7 text-rose-300/80" strokeWidth={1.5} />
          </div>
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/30 text-[9px] uppercase tracking-widest text-rose-200">
            CLOSED
          </div>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        className="absolute top-4 right-4 flex items-center gap-1.5 glass-strong border border-white/10 rounded-full px-2.5 py-1 animate-floaty-slow"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
        <span className="text-[10px] text-rose-200 font-medium">No customers</span>
      </motion.div>
    </div>
  );
}

function SceneGoOnline() {
  return (
    <div className="relative h-52 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#140a2b] via-[#0d0a22] to-[#0a0a1a]">
      <div className="absolute inset-0 radial-glow-violet blur-2xl opacity-70" />
      <div className="absolute left-6 bottom-6">
        <RobotMascot />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.button
          initial={{ scale: 0.94, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="relative px-5 py-2.5 rounded-full btn-primary-grad text-white text-sm font-semibold shadow-2xl shadow-violet-500/50 flex items-center gap-2 animate-pulse-glow"
        >
          <Rocket className="w-4 h-4" /> Go Online
        </motion.button>
        {/* animated cursor */}
        <motion.div
          initial={{ x: -110, y: 40, opacity: 0 }}
          whileInView={{ x: [-110, 6, 6, 6], y: [40, -2, -6, -6], opacity: [0, 1, 1, 1] }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, delay: 0.6, times: [0, 0.6, 0.8, 1] }}
          className="absolute"
        >
          <MousePointer2 className="w-5 h-5 text-white drop-shadow" strokeWidth={2.2} fill="white" />
          <motion.span
            initial={{ opacity: 0, scale: 0.3 }}
            whileInView={{ opacity: [0, 0.8, 0], scale: [0.3, 2.4, 2.6] }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 1.9 }}
            className="absolute -inset-6 rounded-full border border-violet-300/70"
          />
        </motion.div>
      </div>
      {[
        { top: "20%", left: "30%" },
        { top: "32%", right: "26%" },
        { bottom: "26%", left: "40%" },
        { bottom: "22%", right: "32%" },
      ].map((p, i) => (
        <Sparkles
          key={i}
          className="absolute w-3 h-3 text-violet-200 animate-pulse"
          style={{ ...p, animationDelay: `${i * 0.2}s` }}
        />
      ))}
    </div>
  );
}

function SceneWebsite() {
  return (
    <div className="relative h-52 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0b0f2e] via-[#0c0820] to-[#0a0a1a]">
      <div className="absolute inset-0 radial-glow-cyan blur-2xl opacity-60" />
      <div className="absolute right-5 bottom-5">
        <RobotMascot state="happy" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center gap-4 px-6 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.94 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex-1 h-32 rounded-lg border border-white/15 bg-[#0a0b1e] p-2 shadow-2xl shadow-violet-500/25"
        >
          <div className="flex items-center gap-1 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400/70" />
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400/70" />
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/70" />
          </div>
          <div className="h-2 rounded bg-white/10 w-3/4 mb-1" />
          <div className="h-3 rounded bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 mb-1.5" />
          <div className="grid grid-cols-3 gap-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="aspect-square rounded bg-white/5 border border-white/5" />
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="shrink-0"
        >
          <ArrowRight className="w-5 h-5 text-violet-300 animate-floaty" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.9 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-16 h-32 rounded-[14px] border border-white/15 bg-[#0a0b1e] p-1 shadow-xl shadow-pink-500/20 animate-floaty-slow"
        >
          <div className="w-full h-full rounded-[10px] bg-gradient-to-b from-[#13082a] to-[#0a0a1a] relative overflow-hidden">
            <div className="absolute top-1 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-white/10" />
            <div className="absolute inset-x-1.5 top-4 h-6 rounded-md bg-gradient-to-r from-blue-500/60 to-pink-500/60" />
            <div className="absolute inset-x-1.5 top-11 space-y-1">
              <div className="h-1.5 rounded bg-white/10" />
              <div className="h-1.5 rounded bg-white/10 w-2/3" />
              <div className="h-1.5 rounded bg-white/10 w-4/5" />
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: -6 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.7 }}
        className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] text-emerald-200 font-medium">Live · 2 days</span>
      </motion.div>
    </div>
  );
}

function SceneOrders() {
  const notifs = [
    { icon: ShoppingBag, text: "New order · ₹349", top: "14%", left: "8%" },
    { icon: Wallet, text: "UPI payment ✓", top: "38%", right: "8%" },
    { icon: Truck, text: "Out for delivery", bottom: "22%", left: "12%" },
    { icon: Bell, text: "+2 new reviews", bottom: "16%", right: "8%" },
  ];
  return (
    <div className="relative h-52 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0f0820] via-[#0a0a1a] to-[#1a0830]">
      <div className="absolute inset-0 radial-glow-violet blur-2xl opacity-60" />
      <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
        <div className="w-20 h-20 rounded-2xl btn-primary-grad flex items-center justify-center shadow-2xl shadow-violet-500/50 animate-floaty">
          <ShoppingBag className="w-9 h-9 text-white" strokeWidth={1.8} />
        </div>
      </div>
      <div className="absolute right-5 bottom-4">
        <RobotMascot state="happy" />
      </div>
      {notifs.map((n, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.6, y: 6 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            delay: 0.2 + i * 0.18,
            type: "spring",
            stiffness: 220,
            damping: 14,
          }}
          style={n}
          className="absolute flex items-center gap-1.5 glass-strong border border-white/10 rounded-full px-2.5 py-1.5 shadow-xl"
        >
          <span className="w-5 h-5 rounded-md btn-primary-grad flex items-center justify-center">
            <n.icon className="w-3 h-3 text-white" />
          </span>
          <span className="text-[10px] font-semibold text-white whitespace-nowrap">{n.text}</span>
        </motion.div>
      ))}
    </div>
  );
}

function SceneGrowth() {
  const bars = [20, 32, 26, 44, 52, 48, 66, 78, 84, 96];
  return (
    <div className="relative h-52 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0a1024] via-[#0c0822] to-[#180a30]">
      <div className="absolute inset-0 radial-glow-cyan blur-2xl opacity-60" />
      <div className="absolute inset-x-5 top-5 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">Today's revenue</p>
          <p className="font-display font-bold text-xl text-white flex items-center mt-0.5">
            <IndianRupee className="w-4 h-4" />54,230
          </p>
        </div>
        <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 px-2 py-0.5 rounded-full">
          <TrendingUp className="w-3 h-3" /> +34%
        </span>
      </div>
      <div className="absolute left-5 right-20 bottom-5 h-20 flex items-end gap-1.5">
        {bars.map((h, i) => (
          <motion.span
            key={i}
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="flex-1 rounded-sm bg-gradient-to-t from-blue-500 via-violet-500 to-pink-400"
          />
        ))}
      </div>
      <div className="absolute right-5 bottom-5">
        <RobotMascot state="happy" />
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
        className="absolute top-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 glass-strong border border-white/10 rounded-full px-2.5 py-1 animate-floaty"
      >
        <Smile className="w-3 h-3 text-emerald-300" />
        <span className="text-[10px] text-white font-medium">Happy owner 🎉</span>
      </motion.div>
    </div>
  );
}

const steps = [
  {
    num: "01",
    icon: Store,
    title: "Offline Shop",
    caption: "Empty Counters · No Customers",
    desc: "A small local shop with paper bills, low footfall and a register that refuses to ring.",
    accent: "text-rose-300",
    badge: "bg-rose-500/15 border-rose-400/30 text-rose-200",
    Scene: SceneOffline,
  },
  {
    num: "02",
    icon: MousePointer2,
    title: "Go Online — One Click",
    caption: "Take Your Business Online",
    desc: "A single click on \"Go Online\" sparks the transformation. Aria, our robotic guide, takes over.",
    accent: "text-violet-300",
    badge: "bg-violet-500/15 border-violet-400/30 text-violet-200",
    Scene: SceneGoOnline,
  },
  {
    num: "03",
    icon: MonitorSmartphone,
    title: "Website + App Appears",
    caption: "Your Shop is Now Online",
    desc: "Your branded website and mobile app go live in just 2 days — designed, hosted and secured.",
    accent: "text-blue-300",
    badge: "bg-blue-500/15 border-blue-400/30 text-blue-200",
    Scene: SceneWebsite,
  },
  {
    num: "04",
    icon: ShoppingBag,
    title: "Orders Coming",
    caption: "Orders Start Pouring In",
    desc: "Notifications pop — UPI payments, cart confirmations, delivery pings. Real customers, real revenue.",
    accent: "text-pink-300",
    badge: "bg-pink-500/15 border-pink-400/30 text-pink-200",
    Scene: SceneOrders,
  },
  {
    num: "05",
    icon: TrendingUp,
    title: "Business Growth",
    caption: "Business Growing Fast 📈",
    desc: "Revenue compounds. Reach multiplies. A small shop becomes a brand — powered by data and automation.",
    accent: "text-emerald-300",
    badge: "bg-emerald-500/15 border-emerald-400/30 text-emerald-200",
    Scene: SceneGrowth,
  },
];

export default function Timeline({ onCta }) {
  return (
    <section
      id="journey"
      data-testid="timeline-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[90%] h-[60%] radial-glow-violet blur-3xl opacity-50" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="uppercase text-xs tracking-[0.24em] text-violet-300 font-semibold flex items-center justify-center gap-2">
            <Bot className="w-3.5 h-3.5 text-pink-300" />
            The transformation journey
          </p>
          <h2
            data-testid="timeline-heading"
            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
          >
            From an empty shop to a{" "}
            <span className="text-gradient-neon">thriving online business</span>.
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            We bring your business online in <span className="text-white font-semibold">2 days</span> — starting at <span className="text-white font-semibold">₹9,999</span>. Watch Aria the AI guide you through every step.
          </p>
        </div>

        <div className="relative mt-16">
          {/* center progress line (desktop) */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-[2px] -translate-x-1/2">
            <div className="h-full w-full bg-gradient-to-b from-rose-500/30 via-violet-500/60 to-emerald-500/50 rounded-full" />
          </div>

          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="space-y-12 md:space-y-20"
          >
            {steps.map((s, i) => {
              const leftSide = i % 2 === 0;
              return (
                <motion.div
                  key={s.num}
                  variants={fadeUp}
                  data-testid={`timeline-step-${i}`}
                  className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center"
                >
                  <span className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full btn-primary-grad items-center justify-center shadow-[0_0_20px_rgba(167,139,250,0.7)] z-10">
                    <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  </span>

                  <div className={`${leftSide ? "md:order-1 md:pr-10 md:text-right" : "md:order-2 md:pl-10"}`}>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border ${s.badge}`}
                    >
                      Step {s.num}
                    </span>
                    <h3 className={`mt-3 font-display text-2xl sm:text-3xl font-bold ${s.accent}`}>
                      {s.title}
                    </h3>
                    <p className="mt-1.5 font-display text-lg text-white/90 font-semibold">
                      “{s.caption}”
                    </p>
                    <p
                      className="mt-3 text-sm text-slate-400 leading-relaxed max-w-md"
                      style={{ marginLeft: leftSide ? "auto" : undefined }}
                    >
                      {s.desc}
                    </p>
                    <div className={`mt-4 flex ${leftSide ? "md:justify-end" : "md:justify-start"}`}>
                      <div className="w-11 h-11 rounded-xl glass border border-white/10 flex items-center justify-center animate-floaty-slow">
                        <s.icon className={`w-5 h-5 ${s.accent}`} strokeWidth={1.8} />
                      </div>
                    </div>
                  </div>

                  <div className={`${leftSide ? "md:order-2 md:pl-10" : "md:order-1 md:pr-10"}`}>
                    <s.Scene />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="relative mt-20 rounded-3xl glass-strong border border-white/10 p-8 sm:p-12 text-center overflow-hidden"
          data-testid="timeline-final-cta"
        >
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[80%] h-[120%] radial-glow-violet blur-3xl opacity-70" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs uppercase tracking-[0.2em] text-violet-200">
              <Rocket className="w-3.5 h-3.5 text-pink-300" />
              Your turn
            </div>
            <h3 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Take Your Business Online Today{" "}
              <motion.span
                animate={{ y: [0, -6, 0], rotate: [0, -6, 0] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block"
              >
                🚀
              </motion.span>
            </h3>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              We bring your business online in <span className="text-white font-semibold">2 days</span> — starting at <span className="text-white font-semibold">₹9,999</span>. No tech knowledge needed.
            </p>
            <div className="mt-8 flex justify-center">
              <motion.button
                onClick={onCta}
                data-testid="timeline-final-cta-button"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="group relative px-8 py-4 rounded-full btn-primary-grad text-white font-semibold text-base animate-pulse-glow shadow-2xl shadow-violet-500/50 flex items-center gap-2"
              >
                <Rocket className="w-4 h-4" />
                Get Started
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
